/**
 * AtlasFireModule — the fire system's driver (fire realism R1–R4, R12, 2026-09-25).
 *
 *   · THE LIGHT BROKER: one session-fixed pool of point lights (lib/vfx/light_broker.ts) attached to the scene at
 *     init, sized by the tier (`fireLights` + 4 impact + 1 flash = 6–8). The Wilds impact pool, the fireworks flash
 *     and the explosions draw from it (THE LIGHT-SET IDENTITY LAW: created here, never toggled, parked at 0).
 *     Registered BEFORE WildsModule so the impact pool finds it.
 *   · THE FIRE SYSTEM (lib/vfx/fire_system.ts): every registered fire's rung, flicker, light, embers, smoke,
 *     pops, char, haze, crackle — ticked here in RenderSub after the sky (5), the weather (22) and the fireworks
 *     (26), so this frame's night, wind and flashes are current.
 * Levers: `__fire()` (fires, rungs, lights, particles), `__fire().sample()` (the nearest fire's flicker vs its light),
 * `__fire().test(kind?)` (a fire 4 m ahead of the player), `__fire().boom()` (a staged ground explosion ahead),
 * `__lightBroker()` (who holds each pool slot).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { uNightFactor, uSunDir } from './SkyModule.js';
import { windVector } from '../lib/wind.js';
import { activePreset, activeTier } from '../lib/quality_tiers.js';
import { LightBroker } from '../lib/vfx/light_broker.js';
import { FireSystem, setFireSystem, type FireEnv } from '../lib/vfx/fire_system.js';
import { brokerCapacityFor, FIRE_STEPS, type FireKind } from '../lib/vfx/fire_rules.js';
import { groundYIn } from '../lib/grounding.js';
import { CombatVfx } from './living-events/CombatVfx.js';
import { createPlumeMaterial, plumeGeometry, type PlumeHandle } from '../lib/vfx/plume_material.js';
import { SmokeColumns, SMOKE_COLUMN_NEAR_M, type SmokeColumnSource } from '../lib/vfx/smoke_columns.js';
import { settlementSites } from '../../../shared/settlements.js';
import { townSlots } from '../../../shared/town_layout.js';
import { commonsSeedNow } from '../lib/town_names.js';
import { tightenReadback } from '../lib/readback.js';

export class AtlasFireModule implements IModule {
  readonly name = 'AtlasFire';
  private app!: App;
  private broker: LightBroker | null = null;
  private system: FireSystem | null = null;
  private last = 0;
  private readonly _cam = new THREE.Vector3();
  private testN = 0;
  /** The lever's own combat effects (the probe's forced explosion). */
  private vfx: CombatVfx | null = null;
  /** Probe lever: freeze the fire clock (the fireball, the rings, the flashes hold their stage for a picture). */
  private frozen = false;
  private plumeTest: { mesh: THREE.Mesh; fx: PlumeHandle } | null = null;
  /** R10: the large smoke columns (battles, crash sites, the ore works' chimneys) — one instanced batch. */
  private columns: SmokeColumns | null = null;
  private columnSources: SmokeColumnSource[] = [];
  private columnsAt = 0;
  private nearColumns = new Set<string>();
  private chimneys: { seed: string; list: Array<{ key: string; x: number; z: number }> } | null = null;

  init(app: App): void {
    this.app = app;
    const scene = app.world.scene;
    const p = activePreset();
    const fireLights = Math.max(1, Math.min(3, Math.round(p.fireLights ?? 2)));
    this.broker = LightBroker.attach(scene, brokerCapacityFor(fireLights));
    this.broker.setQuota('fire', fireLights);
    this.system = new FireSystem(app, this.broker);
    setFireSystem(this.system);
    app.world.addSystem(makeSystem('AtlasFire', Phase.RenderSub, 27, (dt) => this.tick(dt)));
    bus.on('atlas:world-cleared', () => this.system?.clearAll());
    const w = window as unknown as Record<string, unknown>;
    w.__fire = () => ({
      ...(this.system?.describe() ?? {}),
      quality: activePreset().fireQuality, fireLights, broker: this.broker?.capacity ?? 0,
      webgpu: !!(this.app.renderer as { isWebGPU?: boolean }).isWebGPU,
      /** Probe lever: pin the local weather ('clear', 'storm', …; null releases). */
      weather: (k: string | null) => { (this.app.getModule('Weather') as { setDebugWeather?(w: string | null): void } | undefined)?.setDebugWeather?.(k); return k; },
      sample: () => this.system?.sampleNearest() ?? null,
      test: (kind: FireKind = 'campfire', ahead = 4) => this.testFire(kind, ahead),
      boom: (scale = 1.4, ahead = 14) => this.boom(scale, ahead),
      clearTests: () => { for (let i = 1; i <= this.testN; i++) this.system?.remove(`test:${i}`); },
      screen: (key: string) => { const cam = this.app.renderer.cameras.main as THREE.Camera | undefined; return cam ? this.system?.screenBox(key, cam) ?? null : null; },
      freeze: (on = true) => { this.frozen = on; return on; },
      combat: () => this.vfx ? { ...this.vfx.stats, live: this.vfx.count } : null,
      /** A standalone plume `ahead` m in front at eye height, seen side-on, at `throttle` (the Mach diamonds' picture). */
      plume: (throttle = 1, ahead = 5, len = 4) => this.plumeLever(throttle, ahead, len),
      /** Black-frame evidence levers: a fire's live shader inputs, a child toggle, hide all fires, force the haze off. */
      diag: (key: string) => this.system?.diag(key) ?? null,
      part: (key: string, name: string, visible: boolean) => this.system?.debugPart(key, name, visible) ?? 0,
      hide: (on = true) => { if (this.system) this.system.debugHide = on; return on; },
      noHaze: (on = true) => { if (this.system) this.system.debugNoHaze = on; return on; },
      hazeStrength: (v: number | null = null) => { if (this.system) this.system.debugHazeStrength = v; return v; },
      /** The scene through the main camera WITHOUT the post chain, read back as LINEAR HDR: non-finite pixels counted. */
      hdr: (maxW = 320, key?: string, png = false) => this.hdrProbe(maxW, key, png),
      /**
       * The A/B lever (session 3): `physical(false)` = the PRE-CURVE fixed units (the flame, coals, embers, smoke glow,
       * soot light and pool light as session 1 tuned them) for a "before" picture in the same page; true = on the curve.
       */
      physical: (on = true) => { if (this.system) this.system.physical = !!on; return !!on; },
    });
    this.columns = new SmokeColumns();
    scene.add(this.columns.mesh);
    w.__smokeColumns = () => ({ count: this.columns?.count ?? 0, sources: this.columnSources.map((c) => ({ key: c.key, h: c.height, s: +c.strength.toFixed(2), d: Math.round(Math.hypot(c.x - atlas.localX, c.z - atlas.localZ)) })), near: [...this.nearColumns] });
    w.__lightBroker = () => ({ capacity: this.broker?.capacity, active: this.broker?.active, intact: this.broker?.membershipIntact, slots: this.broker?.describe(), stats: this.broker?.stats });
  }

  private ground(x: number, z: number): number { return atlas.conn ? groundYIn(atlas.conn, atlas.currentWorldId, x, z) : 0; }

  /**
   * Probe lever: render the scene (main camera, no post chain) into an RGBA16F target and read it back as linear HDR.
   * A NaN / Inf written by a scene material shows here as a count; whether the POST chain spreads it is a separate
   * question (compare with `__frameCapture`). Returns the whole frame's stats and, for `key`, the fire's flame box.
   */
  private async hdrProbe(maxW: number, key?: string, png = false): Promise<Record<string, unknown>> {
    const native = this.app.renderer.native;
    const cam = this.app.renderer.cameras.main as THREE.Camera | undefined;
    if (!cam) return { err: 'no camera' };
    const size = native.getDrawingBufferSize(new THREE.Vector2());
    const W = Math.max(16, Math.min(Math.round(size.x), maxW)), H = Math.max(9, Math.round(W * size.y / Math.max(1, size.x)));
    const rt = new THREE.RenderTarget(W, H, { type: THREE.HalfFloatType });
    const prev = native.getRenderTarget();
    try {
      native.setRenderTarget(rt);
      native.render(this.app.world.scene, cam);
      native.setRenderTarget(prev);
      // THE READBACK LAW (lib/readback.ts): WebGPU pads rows to 256 B and hands back the padded buffer.
      const px = tightenReadback(await native.readRenderTargetPixelsAsync(rt, 0, 0, W, H) as ArrayLike<number> & { constructor: { name: string } }, W, H);
      const half = px instanceof Uint16Array;
      const at = (i: number): number => (half ? THREE.DataUtils.fromHalfFloat((px as Uint16Array)[i]) : px[i]);
      const flip = !(native as unknown as { backend?: { isWebGPUBackend?: boolean } }).backend?.isWebGPUBackend;
      const box = key ? this.system?.screenBox(key, cam)?.flame ?? null : null;
      let nan = 0, inf = 0, maxL = 0, sum = 0, n = 0, bn = 0, bNan = 0, bMax = 0, firstNan: number[] | null = null;
      const img = png ? new Uint8ClampedArray(W * H * 4) : null;
      for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
        const sy = flip ? H - 1 - y : y, i = (sy * W + x) * 4;
        const r = at(i), g = at(i + 1), b = at(i + 2), a = at(i + 3);
        const bad = Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b) || Number.isNaN(a);
        const huge = !bad && (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b) || !Number.isFinite(a));
        if (bad) { nan++; if (!firstNan) firstNan = [x, y]; }
        if (huge) inf++;
        const l = bad || huge ? 0 : 0.2126 * r + 0.7152 * g + 0.0722 * b;
        sum += l; n++; if (l > maxL) maxL = l;
        if (box && x / W >= box[0] && x / W <= box[2] && y / H >= box[1] && y / H <= box[3]) { bn++; if (bad || huge) bNan++; if (l > bMax) bMax = l; }
        if (img) { const o = (y * W + x) * 4, e = 0.85 * 4; const tm = (v: number): number => 255 * Math.pow(Math.max(0, bad || huge ? 0 : v * e) / (1 + Math.max(0, v * e)), 1 / 2.2); img[o] = bad ? 255 : tm(r); img[o + 1] = bad ? 0 : tm(g); img[o + 2] = bad ? 255 : tm(b); img[o + 3] = 255; }
      }
      let pngB64 = '';
      if (img) {
        const cv = new OffscreenCanvas(W, H); cv.getContext('2d')!.putImageData(new ImageData(img, W, H), 0, 0);
        const blob = await cv.convertToBlob({ type: 'image/png' });
        pngB64 = await new Promise<string>((res) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(',')[1] ?? ''); fr.readAsDataURL(blob); });
      }
      return { w: W, h: H, half, nan, inf, firstNan, maxL: +maxL.toFixed(2), mean: +(sum / Math.max(1, n)).toFixed(3), box, boxN: bn, boxBad: bNan, boxMaxL: +bMax.toFixed(2), png: pngB64 };
    } catch (e) {
      return { err: String((e as Error)?.message ?? e).slice(0, 300) };
    } finally { native.setRenderTarget(prev); rt.dispose(); }
  }

  /** Probe lever: a staged ground explosion `ahead` metres in front of the player, on this module's own CombatVfx. */
  private boom(scale: number, ahead: number): { x: number; y: number; z: number } {
    const x = atlas.localX - Math.sin(atlas.localYaw) * ahead, z = atlas.localZ - Math.cos(atlas.localYaw) * ahead;
    const y = this.ground(x, z) + 0.4;
    if (!this.vfx) { this.vfx = new CombatVfx(); this.app.world.scene.add(this.vfx.root); }
    else if (!this.vfx.root.parent) this.app.world.scene.add(this.vfx.root);
    this.vfx.explosion(new THREE.Vector3(x, y, z), scale);
    return { x, y, z };
  }

  private plumeLever(throttle: number, ahead: number, len: number): { x: number; y: number; z: number } | null {
    if (throttle <= 0) { if (this.plumeTest) { this.plumeTest.mesh.removeFromParent(); this.plumeTest = null; } return null; }
    if (!this.plumeTest) {
      const fx = createPlumeMaterial({ color: 0x8fd0ff, nozzleRadius: 0.32, palette: 'tint', intensity: 5, physical: true }); // surfaces (2026-09-27): §4.3 on the curve
      const mesh = new THREE.Mesh(plumeGeometry(), fx.material);
      mesh.name = 'plume-test';
      mesh.frustumCulled = false;
      mesh.renderOrder = 6;
      this.app.world.scene.add(mesh);
      this.plumeTest = { mesh, fx };
    }
    const yaw = atlas.localYaw;
    const x = atlas.localX - Math.sin(yaw) * ahead, z = atlas.localZ - Math.cos(yaw) * ahead, y = atlas.localY + 1.4;
    const m = this.plumeTest.mesh;
    // Across the view (the plume's +Z along the camera's right), its mouth to the left of centre.
    const rx = Math.cos(yaw), rz = -Math.sin(yaw);
    m.position.set(x - rx * len / 2, y, z - rz * len / 2);
    m.lookAt(m.position.x + rx, y, m.position.z + rz);
    m.scale.set(0.32, 0.32, len);
    this.plumeTest.fx.set({ throttle, length: len, boost: throttle > 0.95 });
    return { x, y, z };
  }

  /**
   * R10: where the world implies big smoke — an ENGAGED battle (two columns over its target), a battle RECORD
   * resolved in the last 25 min (thinning as it ages), a gunship DOWN in a field (its crash site), and the ore
   * works of every settlement within 3.5 km (a thin chimney plume). Reads the rows already subscribed; adds none.
   */
  private scanColumns(): SmokeColumnSource[] {
    const out: SmokeColumnSource[] = [];
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return out;
    const px = atlas.localX, pz = atlas.localZ;
    const db = conn.db as unknown as {
      battle?: { iter(): Iterable<{ battleId: bigint; worldId: bigint; phase: string; targetX: number; targetZ: number }> };
      battleRecord?: { iter(): Iterable<{ battleId: bigint; worldId: bigint; x: number; z: number; resolvedAt: { microsSinceUnixEpoch: bigint } }> };
      warAirUnit?: { iter(): Iterable<{ airId: bigint; worldId: bigint; state: string; crashX: number; crashZ: number }> };
    };
    const far = 4500;
    const nowMs = Date.now();
    try {
      for (const b of db.battle?.iter() ?? []) {
        if (b.worldId !== wid || b.phase !== 'engaged' || Math.hypot(b.targetX - px, b.targetZ - pz) > far) continue;
        for (const [k, ox, oz] of [[0, 0, 0], [1, 22, -14]] as const) {
          const x = b.targetX + ox, z = b.targetZ + oz;
          out.push({ key: `battle:${b.battleId}:${k}`, x, y: this.ground(x, z), z, height: 150 - k * 30, width: 26 - k * 6, strength: 0.95, glow: 0.55 });
        }
      }
      for (const r of db.battleRecord?.iter() ?? []) {
        if (r.worldId !== wid || Math.hypot(r.x - px, r.z - pz) > far) continue;
        const ageMin = (nowMs - Number(r.resolvedAt.microsSinceUnixEpoch / 1000n)) / 60000;
        if (ageMin < 0 || ageMin > 25) continue;
        const k = 1 - ageMin / 25;
        out.push({ key: `record:${r.battleId}`, x: r.x, y: this.ground(r.x, r.z), z: r.z, height: 70 + 50 * k, width: 16 + 8 * k, strength: 0.25 + 0.6 * k, glow: 0.25 * k });
      }
      for (const a of db.warAirUnit?.iter() ?? []) {
        if (a.worldId !== wid || a.state !== 'down' || Math.hypot(a.crashX - px, a.crashZ - pz) > far) continue;
        out.push({ key: `wreck:${a.airId}`, x: a.crashX, y: this.ground(a.crashX, a.crashZ), z: a.crashZ, height: 85, width: 13, strength: 0.85, glow: 0.8 });
      }
      // The ore works' chimneys (Commons towns): computed once per seed, filtered by distance.
      const seed = commonsSeedNow();
      if (seed && wid === atlas.commonsWorldId) {
        if (!this.chimneys || this.chimneys.seed !== seed) {
          const list: Array<{ key: string; x: number; z: number }> = [];
          for (const site of settlementSites(seed)) for (const slot of townSlots(seed, site)) if (slot.fn === 'industry') list.push({ key: `works:${site.id}:${slot.key}`, x: slot.x, z: slot.z });
          this.chimneys = { seed, list };
        }
        for (const c of this.chimneys.list) {
          if (Math.hypot(c.x - px, c.z - pz) > 3500) continue;
          out.push({ key: c.key, x: c.x, y: this.ground(c.x, c.z) + 12, z: c.z, height: 48, width: 5, strength: 0.42, glow: 0 });
        }
      }
    } catch { /* a table not published yet: no columns from it */ }
    return out;
  }

  /** Near a column (≤ 150 m) the fire system's smoke carries it: a smoke-only fire per source, gone when it is. */
  private nearSmoke(sys: FireSystem): void {
    const keep = new Set<string>();
    for (const c of this.columnSources) {
      const d = Math.hypot(c.x - atlas.localX, c.z - atlas.localZ);
      if (d > SMOKE_COLUMN_NEAR_M * 1.15 || c.key.startsWith('works:')) continue;
      const key = `col:${c.key}`;
      keep.add(key);
      if (!this.nearColumns.has(key)) { sys.add(key, { x: c.x, y: c.y + 0.5, z: c.z, kind: 'afterfire', flame: false, size: Math.min(3, Math.max(1, c.width / 8)), groundY: c.y }); this.nearColumns.add(key); }
    }
    for (const k of [...this.nearColumns]) if (!keep.has(k)) { sys.remove(k); this.nearColumns.delete(k); }
  }

  private testFire(kind: FireKind, ahead: number): { key: string; x: number; y: number; z: number } | null {
    if (!this.system) return null;
    const x = atlas.localX - Math.sin(atlas.localYaw) * ahead, z = atlas.localZ - Math.cos(atlas.localYaw) * ahead;
    const y = this.ground(x, z);
    const key = `test:${++this.testN}`;
    this.system.add(key, { x, y: y + (kind === 'campfire' ? 0.12 : 0), z, kind, bed: kind === 'campfire', groundY: y });
    return { key, x, y, z };
  }

  private tick(dt: number): void {
    const sys = this.system;
    if (!sys) return;
    const now = performance.now();
    const wall = this.last ? Math.min(0.1, (now - this.last) / 1000) : Math.min(0.1, dt || 1 / 60);
    this.last = now;
    const scene = this.app.world.scene;
    const cam = this.app.renderer.cameras.main as THREE.Camera | undefined;
    if (cam) cam.getWorldPosition(this._cam).sub(scene.position); // render space → ABSOLUTE
    else this._cam.set(atlas.localX, atlas.localY, atlas.localZ);
    const p = activePreset();
    const webgpu = !!(this.app.renderer as { isWebGPU?: boolean }).isWebGPU;
    const tier = activeTier();
    const steps = !webgpu ? 12 : tier === 'high' || tier === 'ultra' ? FIRE_STEPS.high : FIRE_STEPS.med;
    let hazeOk = !webgpu;
    if (webgpu) { try { hazeOk = localStorage.getItem('atlas:haze-webgpu') === 'on'; } catch { hazeOk = false; } }
    const env: FireEnv = {
      camera: cam ?? null,
      cam: { x: this._cam.x, y: this._cam.y, z: this._cam.z },
      night: (uNightFactor as unknown as { value: number }).value ?? 0,
      sunDir: uSunDir.value as unknown as THREE.Vector3,
      wind: { x: windVector.x, z: windVector.y, s: windVector.z },
      fogDensity: (scene.fog as { density?: number } | null)?.density ?? 0,
      quality: p.fireQuality ?? 'volume',
      particleCap: p.particleCap ?? 0,
      haze: !!p.fireHaze && !!p.vfxShaders && hazeOk,
      steps,
      inWorld: atlas.currentWorldId !== null && !atlas.spaceFlight,
    };
    const step = this.frozen ? 0 : wall;
    sys.update(step, env, { litSmoke: !!p.litSmoke, webgpu });
    this.vfx?.update(step);
    if (this.columns) {
      if (!env.inWorld || env.quality === 'sprite') { if (this.columns.count) this.columns.write([], env.cam, env.wind); }
      else {
        if (now - this.columnsAt > 2000) { this.columnsAt = now; this.columnSources = this.scanColumns(); this.nearSmoke(sys); }
        this.columns.write(this.columnSources, env.cam, env.wind);
      }
    }
  }

  dispose(): void {
    this.system?.dispose();
    setFireSystem(null);
    this.broker?.dispose();
  }
}
